import { Listbox, Transition } from "@headlessui/react";
import { SwitchVerticalIcon } from "@heroicons/react/outline";
import { CogIcon } from "@heroicons/react/solid";
import classNames from "classnames";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterPress } from "../../models/filter-press";
import CardTooltip from "../card-tooltip";

interface UpcomingReplacementsStatProps {
  press: FilterPress;
}

const UpcomingReplacementsStat: React.FC<UpcomingReplacementsStatProps> = ({
  press,
}) => {
  const { t } = useTranslation();

  const timeframeOptions = [
    { name: t("week"), value: "week" },
    { name: t("month"), value: "month" },
  ];

  const [timeframe, setTimeframe] = useState(timeframeOptions[0]);

  const upcomingReplacements =
    press.upcomingReplacements?.[timeframe.value as "week" | "month"];

  if (typeof upcomingReplacements === "undefined") {
    return null;
  }

  return (
    <dl className="relative bg-white py-5 px-4 sm:p-6 shadow rounded-lg flex gap-x-3 justify-between items-start">
      <div>
        {/* Icon & title */}
        <dt>
          <div className="absolute bg-andritz-500 rounded-md p-3">
            <SwitchVerticalIcon
              className="h-6 w-6 text-white"
              aria-hidden="true"
            />
          </div>
          <p className="ml-16 text-sm font-medium text-gray-500 truncate">
            {t("stat-upcoming-replace-title")}
          </p>
        </dt>

        {/* Content */}
        <dd className="ml-16 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {/* Stat */}
          <p className="text-sm text-gray-900 font-semibold">
            <span className="text-2xl leading-tight">
              {upcomingReplacements}
            </span>{" "}
            {t("stat-upcoming-replace-unit", { count: upcomingReplacements })}
          </p>
        </dd>
      </div>

      {/* Settings & Info */}
      <div className="flex gap-2 items-center -mt-2.5 sm:-mt-3 sm:-mr-3">
        <Listbox
          as="div"
          value={timeframe}
          onChange={setTimeframe}
          className="flex-shrink-0"
        >
          {({ open }) => (
            <>
              <Listbox.Label className="sr-only">
                {t("select-timeframe")}
              </Listbox.Label>
              <div className="relative">
                <Listbox.Button className="relative inline-flex items-center rounded-full p-2 bg-gray-100 text-sm font-medium text-gray-500 whitespace-nowrap hover:bg-gray-200 sm:py-1.5 sm:px-3">
                  <CogIcon
                    className="flex-shrink-0 h-5 w-5 sm:-ml-1"
                    aria-hidden="true"
                  />

                  <span className="hidden truncate sm:ml-2 sm:block text-gray-900">
                    {timeframe.name}
                  </span>
                </Listbox.Button>
                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Listbox.Options className="origin-top-right absolute right-0 top-full z-10 mt-1 w-48 bg-white shadow-lg rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {timeframeOptions.map((timeframeOption) => (
                      <Listbox.Option
                        key={timeframeOption.value}
                        className={({ active }) =>
                          classNames(
                            active ? "bg-gray-100" : "bg-white",
                            "cursor-pointer select-none relative py-2 px-3"
                          )
                        }
                        value={timeframeOption}
                      >
                        <div className="flex items-center">
                          <span className="block font-medium truncate">
                            {timeframeOption.name}
                          </span>
                        </div>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>

        <CardTooltip>
          <p className="text-sm text-gray-500">
            {t("stat-upcoming-replace-info")}
          </p>
        </CardTooltip>
      </div>
    </dl>
  );
};

export default UpcomingReplacementsStat;
